import { useEffect, useRef } from 'react';
import { supabase } from '../../supabase';

const BURST_MS = 500;

/**
 * KAN-736 / KAN-1046 — live "a row was deleted" signal.
 *
 * Supabase Realtime skips RLS on DELETE events, so the publication no longer
 * emits them. Instead a trigger bumps ONE data-free row in
 * `public.row_delete_pings` (id, tenant_id | user_id, scope, bumped_at) per
 * delete statement, and this hook turns that INSERT/UPDATE into
 * `onDelete(scope)` so the consumer runs its silent reconcile for that
 * collection. Members read their tenant's rows; a user reads their own.
 *
 * Scopes: tenant rows — customers, jobs, job_shifts, no_service, office_tasks,
 * document_delivery_issues; user rows — user_notice_receipts.
 *
 * ONE PRIVATE channel per owner (`tenant_id=X` or `user_id=Y`), ref-counted and
 * shared by every mounted hook instance (same shape as `companyAccessLive`). The
 * channel is created by the first instance and removed by the last. Each
 * instance only receives the scopes it asked for, and bursts dedupe PER
 * SUBSCRIBER PER SCOPE: at most one `onDelete(scope)` per BURST_MS, and an event
 * inside the window schedules exactly one trailing call. The filter is the
 * owner equality only (RLS on row_delete_pings is the boundary). No subscription
 * without a tenant/user.
 *
 * DESIGN NOTE (later, not built): the `*_pings` tables are row_delete_pings,
 * company_access_pings, security_event_pings, feature_change_pings,
 * user_notice_pings, route_change_pings, credit_mutation_pings,
 * platform_cancellation_pings (+ office/delivery via row_delete_pings scopes).
 * Proposal: one `pings` table (owner, kind, bumped_at) and ONE channel per owner
 * with a kind -> handler registry (this file's registry generalised). Risks: a
 * single table makes every trigger write the same hot row set; per-kind RLS
 * (admin-only security events, user-only route pings) must be re-expressed in
 * ONE policy or the channel splits by audience anyway; the cutover needs a
 * dual-write window so open older bundles keep working (appCompat level); and a
 * dropped combined channel loses every notice at once, so resync-on-resubscribe
 * must fan out to all kinds.
 *
 * @param {{ tenantId?: string|null, userId?: string|null, scopes: string[],
 *           enabled?: boolean, onDelete: (scope: string) => void }} opts
 *   Pass `tenantId` for tenant scopes OR `userId` for user scopes (not both).
 */
const TEARDOWN_GRACE_MS = 1500;
const owners = new Map(); // `${ownerCol}-${ownerId}` -> { channel, subs: Set }

/** Test-only: drop every owner entry and pending teardown so suites don't bleed. */
export function __resetRowDeletePing() {
  owners.forEach((e) => { if (e.teardown) clearTimeout(e.teardown); });
  owners.clear();
}

function registerSubscriber(ownerCol, ownerId, sub) {
  const key = `${ownerCol}-${ownerId}`;
  let entry = owners.get(key);
  if (!entry) {
    const e = { channel: null, subs: new Set(), teardown: null };
    e.channel = supabase
      .channel(`row-delete-ping-${key}`, { config: { private: true } })
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'row_delete_pings', filter: `${ownerCol}=eq.${ownerId}`,
      }, (payload) => {
        if (payload?.eventType === 'DELETE') return;
        const scope = payload?.new?.scope;
        if (!scope) return;
        e.subs.forEach((s) => { if (s.wanted.has(scope)) s.ping(scope); });
      })
      .subscribe();
    owners.set(key, e);
    entry = e;
  }
  // Reuse a live channel that is waiting out its grace period (remount / StrictMode).
  if (entry.teardown) { clearTimeout(entry.teardown); entry.teardown = null; }
  entry.subs.add(sub);
  return () => {
    sub.stop();
    const cur = owners.get(key);
    if (!cur) return;
    cur.subs.delete(sub);
    if (cur.subs.size > 0) return;
    // realtime-js hands back a not-yet-left channel for the same topic and subscribe()
    // on it is a no-op, so an immediate remount would get a dead channel: wait first.
    cur.teardown = setTimeout(() => {
      if (cur.subs.size > 0 || owners.get(key) !== cur) return;
      cur.teardown = null;
      supabase.removeChannel(cur.channel);
      owners.delete(key);
    }, TEARDOWN_GRACE_MS);
  };
}

function makeSubscriber(wanted, getOnDelete) {
  const lastRun = {};
  const timers = {};
  let alive = true;
  const run = (scope) => {
    lastRun[scope] = Date.now();
    try { getOnDelete()?.(scope); } catch (_) { /* never break the channel */ }
  };
  return {
    wanted,
    ping(scope) {
      if (!alive) return;
      const elapsed = Date.now() - (lastRun[scope] || 0);
      if (elapsed >= BURST_MS) { run(scope); return; }
      if (!timers[scope]) {
        timers[scope] = setTimeout(() => { timers[scope] = null; if (alive) run(scope); }, BURST_MS - elapsed);
      }
    },
    stop() {
      alive = false;
      Object.values(timers).forEach((t) => { if (t) clearTimeout(t); });
    },
  };
}

export function useRowDeletePing({ tenantId = null, userId = null, scopes, enabled = true, onDelete }) {
  const onDeleteRef = useRef(onDelete);
  onDeleteRef.current = onDelete;
  const scopeKey = (scopes || []).join(',');
  const ownerCol = tenantId ? 'tenant_id' : 'user_id';
  const ownerId = tenantId || userId;

  useEffect(() => {
    if (!enabled || !ownerId || !scopeKey) return undefined;
    const sub = makeSubscriber(new Set(scopeKey.split(',')), () => onDeleteRef.current);
    return registerSubscriber(ownerCol, ownerId, sub);
  }, [enabled, ownerCol, ownerId, scopeKey]);
}

export default useRowDeletePing;
